/**
 * `demo/guide/guideState.ts` — the demo guide's state machine (#2824).
 *
 * Pure functions over a small state value, plus storage per demo token in
 * sessionStorage so a reload keeps the place. Each tour (the introduction and
 * one per page) keeps its own place. A tour with no entry has not been seen:
 * it opens at its first step.
 */
import { type ChapterMap, INTRO_ID, scenarioTourId } from "./chapters";
import type { GuideStep } from "./types";

export type GuideStatus = "open" | "skipped" | "done";

export interface TourPlace {
  stepIndex: number;
  status: GuideStatus;
}

export interface GuideState {
  tours: Record<string, TourPlace>;
  /** The variant of the scenario the visitor picked, or null. */
  activeScenario: string | null;
}

const STORAGE_PREFIX = "tarii.demo_guide.v4:";

const UNSEEN: TourPlace = { stepIndex: 0, status: "open" };

export function initialGuideState(): GuideState {
  return { tours: {}, activeScenario: null };
}

export function placeOf(state: GuideState, tourId: string): TourPlace {
  return state.tours[tourId] ?? UNSEEN;
}

function withPlace(state: GuideState, tourId: string, place: TourPlace): GuideState {
  return { ...state, tours: { ...state.tours, [tourId]: place } };
}

/** The page tour for a pathname, open or not. */
export function pageTourId(chapters: ChapterMap, pathname: string): string | null {
  for (const chapter of Object.values(chapters)) {
    if (chapter.page?.(pathname)) return chapter.id;
  }
  return null;
}

/**
 * The tour to show now: the introduction while it is open, then the open tour
 * of the picked scenario, then the open tour of this page. Null when nothing
 * shows.
 */
export function activeTourId(
  state: GuideState,
  chapters: ChapterMap,
  pathname: string,
): string | null {
  if (placeOf(state, INTRO_ID).status === "open") return INTRO_ID;
  const scenario = state.activeScenario ? scenarioTourId(state.activeScenario) : null;
  if (scenario && chapters[scenario] && placeOf(state, scenario).status === "open") return scenario;
  const page = pageTourId(chapters, pathname);
  if (page && placeOf(state, page).status === "open") return page;
  return null;
}

export function currentStep(
  state: GuideState,
  chapters: ChapterMap,
  tourId: string | null,
): GuideStep | null {
  if (!tourId) return null;
  return chapters[tourId]?.steps[placeOf(state, tourId).stepIndex] ?? null;
}

export function stepCount(chapters: ChapterMap, tourId: string): number {
  return chapters[tourId]?.steps.length ?? 0;
}

export function stepNumber(state: GuideState, tourId: string): number {
  return placeOf(state, tourId).stepIndex + 1;
}

/** Next: one step on. After the last step the tour is done. */
export function goNext(state: GuideState, chapters: ChapterMap, tourId: string): GuideState {
  const chapter = chapters[tourId];
  if (!chapter) return state;
  const place = placeOf(state, tourId);
  if (place.stepIndex + 1 < chapter.steps.length) {
    return withPlace(state, tourId, { ...place, stepIndex: place.stepIndex + 1 });
  }
  return withPlace(state, tourId, { ...place, status: "done" });
}

export function canGoBack(state: GuideState, tourId: string): boolean {
  return placeOf(state, tourId).stepIndex > 0;
}

/** Back: one step back inside the same tour. */
export function goBack(state: GuideState, tourId: string): GuideState {
  if (!canGoBack(state, tourId)) return state;
  const place = placeOf(state, tourId);
  return withPlace(state, tourId, { ...place, stepIndex: place.stepIndex - 1 });
}

/** Skip: closes this tour and keeps its step. Other tours still open on their page. */
export function skip(state: GuideState, tourId: string): GuideState {
  const place = placeOf(state, tourId);
  return place.status === "open"
    ? withPlace(state, tourId, { ...place, status: "skipped" })
    : state;
}

/** Show a tour again: where it stopped, or from its first step when it was done. */
export function reopen(state: GuideState, tourId: string): GuideState {
  const place = placeOf(state, tourId);
  const stepIndex = place.status === "done" ? 0 : place.stepIndex;
  return withPlace(state, tourId, { stepIndex, status: "open" });
}

/** The visitor picked a scenario: it becomes the active one and its tour starts over. */
export function startScenario(state: GuideState, variant: string): GuideState {
  // The scenario takes over at once: an open introduction would otherwise stay in front of it.
  const intro = state.tours[INTRO_ID];
  const tours = { ...state.tours };
  if (!intro || intro.status === "open") {
    tours[INTRO_ID] = { stepIndex: intro?.stepIndex ?? 0, status: "skipped" };
  }
  return {
    tours: { ...tours, [scenarioTourId(variant)]: { stepIndex: 0, status: "open" } },
    activeScenario: variant,
  };
}

function isPlace(value: unknown, steps: number): value is TourPlace {
  if (!value || typeof value !== "object") return false;
  const p = value as Partial<TourPlace>;
  if (p.status !== "open" && p.status !== "skipped" && p.status !== "done") return false;
  return typeof p.stepIndex === "number" && p.stepIndex >= 0 && p.stepIndex < steps;
}

/** The stored places that still fit the chapters. Others are dropped. */
export function usablePlaces(state: unknown, chapters: ChapterMap): GuideState | null {
  if (!state || typeof state !== "object") return null;
  const tours = (state as { tours?: unknown }).tours;
  if (!tours || typeof tours !== "object") return null;
  const out: Record<string, TourPlace> = {};
  for (const [id, place] of Object.entries(tours as Record<string, unknown>)) {
    const chapter = chapters[id];
    if (chapter && isPlace(place, chapter.steps.length)) out[id] = place;
  }
  const active = (state as { activeScenario?: unknown }).activeScenario;
  const activeScenario =
    typeof active === "string" && chapters[scenarioTourId(active)] ? active : null;
  return { tours: out, activeScenario };
}

export function loadGuideState(token: string, chapters: ChapterMap): GuideState {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_PREFIX + token);
    if (raw) {
      const usable = usablePlaces(JSON.parse(raw), chapters);
      if (usable) return usable;
    }
  } catch {
    // Storage blocked or unreadable: start from the top.
  }
  return initialGuideState();
}

export function saveGuideState(token: string, state: GuideState): void {
  try {
    window.sessionStorage.setItem(STORAGE_PREFIX + token, JSON.stringify(state));
  } catch {
    // Storage blocked: the guide still works, it just forgets on reload.
  }
}
